// src/components/ai-prayer/companion/UnifiedChatInterface.tsx
import React, { useState, useEffect, useRef } from 'react';
import { toast } from '@/components/ui/sonner';
import CompanionChatLayout from '@/components/chat/CompanionChatLayout';
import { MessageBubble } from './MessageBubble';
import AutoTextarea from '@/components/ui/auto-textarea';
import { Button } from '@/components/ui/button';
import { Send } from 'lucide-react';
import { ChatMessage, ChatMode } from '@/types/chat';
import { abielChat, type ChatMessage as ApiChatMessage } from './lib/abielApi';
import TTSControls from './TTSControls';
import AbielGuidanceDisplay from './AbielGuidanceDisplay';
import { useSettings } from '@/contexts/settings';
import RiskAlertBanner from './RiskAlertBanner';
import { detectRiskFlag, type RiskFlag } from '@/utils/risk';

interface UnifiedChatInterfaceProps {
  mode?: ChatMode;
  systemPrompt?: string;
  topic?: 'prayer' | 'bible' | 'study';
  /** NEW: allows a seed like "Study John 3:16" or "John 3:16" */
  initialUserMessage?: string;
}

const systemPrompts = {
  regular:
    'You are Abiel, a wise and compassionate AI companion for spiritual conversations. You DO NOT pray for users or generate prayers. Instead, you provide biblical wisdom, answer questions, and guide [...]',
  study:
    'You are Abiel, a Bible study assistant. Help users understand biblical passages, provide context, explain difficult concepts, and facilitate deeper study of Scripture.',
};

const welcomeMessages = {
  regular:
    "💬 Hi! I'm Abiel, your AI spiritual companion. Share your thoughts, questions, or prayer requests. I'm here to listen and provide biblical wisdom.",
  study:
    "📖 Welcome! I'm here to help you study the Bible. Ask questions about passages, get historical context, or explore theological concepts.",
};

const placeholders = {
  regular: "Share what's on your heart...",
  study: "Ask about a passage or concept...",
};

export function UnifiedChatInterface({
  mode,
  systemPrompt,
  topic,
  initialUserMessage = '',
}: UnifiedChatInterfaceProps) {
  const { settings } = useSettings();

  const finalSystemPrompt =
    systemPrompt || (mode ? systemPrompts[mode] : systemPrompts.regular);
  const welcomeMessage = mode ? welcomeMessages[mode] : welcomeMessages.regular;
  const placeholder = mode ? placeholders[mode] : placeholders.regular;
  const finalTopic = topic || (mode === 'study' ? 'study' : 'bible');

  const supportsTTS = mode === 'regular' || mode === 'study';
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      id: 'welcome',
      role: 'assistant',
      content: welcomeMessage,
      timestamp: new Date(),
    },
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const [riskFlag, setRiskFlag] = useState<RiskFlag | null>(null);
  const hasPrefilled = useRef(false);

  useEffect(() => {
    // Only prefill once, and only if we have an initialUserMessage
    if (hasPrefilled.current || !initialUserMessage?.trim()) return;
    
    // Normalize: strip translation suffixes like "(ESV)", "NIV", etc.
    let seed = initialUserMessage.trim().replace(/\s*\(?(ESV|NIV|KJV|NKJV|NLT|NASB)\)?$/i, '').trim();
    
    // If in study mode and it looks like a reference, prepend "Study "
    if (mode === 'study') {
      const looksLikeRef = /^[1-3]?\s?[A-Za-z]+(?:\s[A-Za-z]+)*\s+\d+(?::\d+(?:-\d+)?)?$/.test(seed);
      if (looksLikeRef && !seed.startsWith('Study ')) {
        seed = `Study ${seed}`;
      }
    }
    
    console.log('[UnifiedChatInterface] Setting prefill:', seed);
    setInputValue(seed);
    hasPrefilled.current = true;
  }, [initialUserMessage, mode]);

  useEffect(() => {
    if (riskFlag) {
      toast.error(`⚠️ Sensitive issue detected: ${riskFlag.category.toUpperCase()}`);
    }
  }, [riskFlag]);

  const handleSend = async () => {
    if (!inputValue.trim() || isLoading) return;

    const immediateFlag = detectRiskFlag(inputValue);
    if (immediateFlag && !riskFlag) {
      setRiskFlag(immediateFlag);
    }

    if (mode === 'regular') {
      const prayerKeywords = [
        'pray for','prayer for','please pray','can you pray','would you pray',
        'help me pray','pray with me','let us pray',"let's pray",
      ];
      const containsPrayerRequest = prayerKeywords.some((k) =>
        inputValue.toLowerCase().includes(k),
      );

      if (containsPrayerRequest) {
        const userMessage: ChatMessage = {
          id: Date.now().toString(),
          role: 'user',
          content: inputValue.trim(),
          timestamp: new Date(),
        };

        setMessages((prev) => [...prev, userMessage]);
        setInputValue('');
        setIsLoading(true);

        try {
          const apiMessages: ApiChatMessage[] = [
            {
              role: 'system',
              content:
                'You are Abiel, a Christian prayer companion. Provide thoughtful, Scripture-centered PRAYER GUIDANCE. Do not generate prayers. Instead, guide the user with passages, outlines, and prom[...]',
            },
            { role: 'user', content: userMessage.content },
          ];

          const result = await abielChat(apiMessages, {
            topic: 'guide',
            provider: settings.guidanceProvider,
          });

          if (result?.riskFlag) {
            setRiskFlag(
              typeof result.riskFlag === 'string'
                ? detectRiskFlag(result.riskFlag)
                : result.riskFlag,
            );
          }

          const { parseGuidanceResponse } = await import('@/utils/guidance/guidanceParser');
          const parsedGuidance = parseGuidanceResponse(result);

          const flag = detectRiskFlag(JSON.stringify(parsedGuidance));
          if (flag) setRiskFlag(flag);

          const guidanceMessage: ChatMessage = {
            id: (Date.now() + 1).toString(),
            role: 'assistant',
            content: 'guidance_display',
            timestamp: new Date(),
            guidance: parsedGuidance,
          };

          setMessages((prev) => [...prev, guidanceMessage]);
          setIsLoading(false);
          return;
        } catch (error) {
          console.error('Error generating prayer guidance:', error);
          const errorMessage: ChatMessage = {
            id: (Date.now() + 1).toString(),
            role: 'assistant',
            content: 'I encountered an error generating prayer guidance. Please try again.',
            timestamp: new Date(),
          };
          setMessages((prev) => [...prev, errorMessage]);
          setIsLoading(false);
          return;
        }
      }
    }

    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      role: 'user',
      content: inputValue.trim(),
      timestamp: new Date(),
    };

    const nextMessages = [...messages, userMessage];
    setMessages(nextMessages);
    setInputValue('');
    setIsLoading(true);

    try {
      const apiMessages: ApiChatMessage[] = [
        { role: 'system', content: finalSystemPrompt },
        ...nextMessages.slice(1).map((m) => ({
          role: m.role as 'user' | 'assistant',
          content: m.content,
        })),
      ];

      const result = await abielChat(apiMessages, {
        topic: finalTopic,
        provider: settings.guidanceProvider,
      });

      if (result?.riskFlag) {
        setRiskFlag(
          typeof result.riskFlag === 'string' ? detectRiskFlag(result.riskFlag) : result.riskFlag,
        );
      }

      const flag = detectRiskFlag(result.guidance || result.message || '');
      if (flag) setRiskFlag(flag);

      const assistantMessage: ChatMessage = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: result.guidance || result.message || 'No response',
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, assistantMessage]);
    } catch (error) {
      console.error('Error sending message:', error);
      const errorMessage: ChatMessage = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: 'I encountered an error. Please try again.',
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const footer = (
    <div className="p-4 bg-background/80 backdrop-blur-sm border-t">
      <div className="flex items-end gap-3">
        <AutoTextarea
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onEnter={handleSend}
          placeholder={placeholder}
          disabled={isLoading}
          className="flex-1"
          maxRows={4}
        />
        {supportsTTS && (
          <TTSControls
            text={(() => {
              const last = messages[messages.length - 1];
              return last?.role === 'assistant' ? last.content : null;
            })()}
            disabled={isLoading}
          />
        )}
        <Button
          onClick={handleSend}
          disabled={!inputValue.trim() || isLoading}
          size="sm"
          className="flex-shrink-0"
        >
          <Send className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );

  return (
    <CompanionChatLayout header={null} footer={footer}>
      {riskFlag && <RiskAlertBanner riskFlag={riskFlag} />}

      {messages.map((message) => {
        if (message.content === 'guidance_display' && message.guidance) {
          return (
            <div key={message.id} className="mb-4">
              <AbielGuidanceDisplay guidance={message.guidance} />
            </div>
          );
        }
        return (
          <MessageBubble
            key={message.id || `${message.timestamp}-${message.content.slice(0, 10)}`}
            message={message}
          />
        );
      })}
      {isLoading && (
        <div className="flex justify-start">
          <div className="bg-muted text-foreground rounded-2xl rounded-bl-md px-4 py-3 max-w-[80%]">
            <div className="flex items-center space-x-1">
              <div className="animate-pulse">Abiel is thinking...</div>
            </div>
          </div>
        </div>
      )}
    </CompanionChatLayout>
  );
}

// Also provide a default export so either import style works:
//   import UnifiedChatInterface from '...';
//   import { UnifiedChatInterface } from '...';
export default UnifiedChatInterface;
